/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { useAuthService } from '@/hooks/useAuthService';
import { useTranslation } from '@/lib/i18n';
import { logger } from '@/lib/logger';

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

/**
 * Cray26 Theme - Reset Password Page
 * Modern reset password page with Tailwind CSS styling
 */

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const queryToken = searchParams.get('token');
  const { t } = useTranslation();
  const [tokenValue, setTokenValue] = useState(queryToken || '');
  const authService = useAuthService();

  // If user supplied no token we don't force an error immediately;
  // they can paste one into the field below.  The original logic
  // complained, but we no longer need that useEffect at all.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.resetPassword.passwordMismatch'));
      setIsLoading(false);
      return;
    }

    if (password.length < 6) {
      setError(t('auth.resetPassword.passwordTooShort'));
      setIsLoading(false);
      return;
    }

    try {
      // call backend reset endpoint
      const resp = await authService.resetPasswordCustomer(tokenValue, password);
      if (resp && (resp.ok || resp.ok === undefined)) {
        logger.info({ msg: 'Password reset attempt', hasToken: !!tokenValue });
        setSuccess(true);
      } else {
        throw new Error('reset failed');
      }

      // Redirect to login after a delay
      setTimeout(() => {
        router.push('/login?message=' + encodeURIComponent(t('auth.resetPassword.updateSuccess')));
      }, 2000);
    } catch (err) {
      setError(t('auth.resetPassword.updateError'));
      logger.error({ msg: 'Password reset error', error: err });
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[60vh] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">{t('auth.resetPassword.passwordUpdated')}</h2>
              <p className="mt-2 text-center text-sm text-gray-600">{t('auth.resetPassword.passwordUpdatedMessage')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">{t('auth.resetPassword.title')}</h2>
        <p className="mt-2 text-center text-sm text-gray-600">{t('auth.resetPassword.description')}</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">{error}</div>}

            {/* always show token input so user can paste one or see pre‑filled value */}
            <div>
              <label htmlFor="token" className="block text-sm font-medium text-gray-700">
                {t('auth.resetPassword.token')}
              </label>
              <div className="mt-1">
                <input
                  id="token"
                  name="token"
                  type="text"
                  required
                  value={tokenValue}
                  onChange={(e) => setTokenValue(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('auth.resetPassword.tokenPlaceholder')}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('auth.resetPassword.newPassword')}
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('auth.resetPassword.confirmNewPassword')}
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || !tokenValue}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? t('auth.resetPassword.updating') : t('auth.resetPassword.updatePassword')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
